import { runtimeConfig } from "../config/runtime-config";
import { getSessionToken } from "../session/auth-token";

export type UploadedAttachment = {
  contentType: string;
  createdAt: string;
  downloadKey: string;
  // Relative capability path (/attachments/<id>/<key>/<name>) — this is what
  // gets embedded into document content so it survives host changes.
  downloadPath: string;
  fileName: string;
  id: string;
  sizeBytes: number;
  uploaderUserId: string;
};

const apiBaseUrl = runtimeConfig.apiUrl.replace(/\/trpc\/?$/, "");

// Universal upload: files become loose workspace blobs; the owning feature
// (comment, document, …) claims them afterwards.
export async function uploadAttachment(file: File): Promise<UploadedAttachment> {
  const token = getSessionToken();
  const response = await fetch(`${apiBaseUrl}/attachments`, {
    body: file,
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      "content-type": "application/octet-stream",
      "x-file-name": encodeURIComponent(file.name || "file"),
      "x-file-type": file.type || "application/octet-stream",
    },
    method: "POST",
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? "Could not upload the file.");
  }

  return (await response.json()) as UploadedAttachment;
}

export function attachmentUrl(attachment: { downloadKey: string; fileName: string; id: string }) {
  return `${apiBaseUrl}${attachmentPath(attachment)}`;
}

export function attachmentPath(attachment: { downloadKey: string; fileName: string; id: string }) {
  return `/attachments/${attachment.id}/${attachment.downloadKey}/${encodeURIComponent(attachment.fileName)}`;
}
